import React from "react";
import {
  Box,
  Stack,
  Skeleton,
  useTheme,
  Paper,
  Divider,
} from "@mui/material";
import { keyframes } from "@emotion/react";

// Shimmer animation
const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const StoryManagementChaptersShimmer: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box>
      {/* Header with Add Button Shimmer */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Skeleton
          variant="rectangular"
          width={180}
          height={40}
          sx={{
            borderRadius: 3,
            bgcolor: isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)",
          }}
        />
      </Box>

      {/* Statistics Card Shimmer */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          backgroundColor: "transparent",
          backdropFilter: "blur(12px)",
          border: (theme) =>
            `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)"
            }`,
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 2px 8px rgba(0,0,0,0.1)"
              : "0 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={3}
          divider={
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.08)",
                display: { xs: "none", sm: "block" },
              }}
            />
          }
        >
          {/* Total Chapters Stat */}
          <Box sx={{ flex: 1, textAlign: { xs: "left", sm: "center" } }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              justifyContent={{ xs: "flex-start", sm: "center" }}
            >
              <Skeleton
                variant="circular"
                width={28}
                height={28}
                sx={{
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.08)",
                }}
              />
              <Box>
                <Skeleton
                  variant="text"
                  width={40}
                  height={32}
                  sx={{
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.08)",
                  }}
                />
                <Skeleton
                  variant="text"
                  width={100}
                  height={20}
                  sx={{
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.06)",
                    mt: 0.5,
                  }}
                />
              </Box>
            </Stack>
          </Box>

          {/* Published Count Stat */}
          <Box sx={{ flex: 1, textAlign: { xs: "left", sm: "center" } }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              justifyContent={{ xs: "flex-start", sm: "center" }}
            >
              <Skeleton
                variant="circular"
                width={28}
                height={28}
                sx={{
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.08)",
                }}
              />
              <Box>
                <Skeleton
                  variant="text"
                  width={40}
                  height={32}
                  sx={{
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.08)",
                  }}
                />
                <Skeleton
                  variant="text"
                  width={80}
                  height={20}
                  sx={{
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.06)",
                    mt: 0.5,
                  }}
                />
              </Box>
            </Stack>
          </Box>

          {/* Draft Count Stat */}
          <Box sx={{ flex: 1, textAlign: { xs: "left", sm: "center" } }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              justifyContent={{ xs: "flex-start", sm: "center" }}
            >
              <Skeleton
                variant="circular"
                width={28}
                height={28}
                sx={{
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.08)",
                }}
              />
              <Box>
                <Skeleton
                  variant="text"
                  width={40}
                  height={32}
                  sx={{
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.08)",
                  }}
                />
                <Skeleton
                  variant="text"
                  width={60}
                  height={20}
                  sx={{
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.06)",
                    mt: 0.5,
                  }}
                />
              </Box>
            </Stack>
          </Box>

          {/* Word Count Stat */}
          <Box sx={{ flex: 1, textAlign: { xs: "left", sm: "center" } }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              justifyContent={{ xs: "flex-start", sm: "center" }}
            >
              <Skeleton
                variant="circular"
                width={28}
                height={28}
                sx={{
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.08)",
                }}
              />
              <Box>
                <Skeleton
                  variant="text"
                  width={60}
                  height={32}
                  sx={{
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.08)",
                  }}
                />
                <Skeleton
                  variant="text"
                  width={90}
                  height={20}
                  sx={{
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.06)",
                    mt: 0.5,
                  }}
                />
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {/* Action Buttons Shimmer */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        <Skeleton
          variant="rectangular"
          width={140}
          height={36}
          sx={{
            borderRadius: 3,
            bgcolor: isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)",
          }}
        />
        <Skeleton
          variant="rectangular"
          width={160}
          height={36}
          sx={{
            borderRadius: 3,
            bgcolor: isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)",
          }}
        />
      </Box>

      {/* Chapters List Shimmer */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          backgroundColor: "transparent",
          backdropFilter: "blur(12px)",
          border: (theme) =>
            `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)"
            }`,
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 2px 8px rgba(0,0,0,0.1)"
              : "0 2px 8px rgba(0,0,0,0.05)",
          p: 3,
        }}
      >
        <Stack spacing={2}>
          {Array.from({ length: 3 }).map((_, index) => (
            <React.Fragment key={index}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: isDark
                    ? "rgba(255,255,255,0.02)"
                    : "rgba(0,0,0,0.02)",
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: isDark
                      ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)"
                      : "linear-gradient(90deg, transparent, rgba(0,0,0,0.05), transparent)",
                    animation: `${shimmer} 2s infinite`,
                  },
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                    {/* Chapter Number */}
                    <Skeleton
                      variant="circular"
                      width={40}
                      height={40}
                      sx={{
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.08)",
                      }}
                    />
                    {/* Chapter Title and Info */}
                    <Box sx={{ flex: 1 }}>
                      <Skeleton
                        variant="text"
                        width="60%"
                        height={24}
                        sx={{
                          bgcolor: isDark
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(0,0,0,0.08)",
                          mb: 1,
                        }}
                      />
                      <Stack direction="row" spacing={2}>
                        <Skeleton
                          variant="text"
                          width={80}
                          height={18}
                          sx={{
                            bgcolor: isDark
                              ? "rgba(255,255,255,0.06)"
                              : "rgba(0,0,0,0.06)",
                          }}
                        />
                        <Skeleton
                          variant="text"
                          width={100}
                          height={18}
                          sx={{
                            bgcolor: isDark
                              ? "rgba(255,255,255,0.06)"
                              : "rgba(0,0,0,0.06)",
                          }}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                  {/* Action Buttons */}
                  <Stack direction="row" spacing={1}>
                    <Skeleton
                      variant="rectangular"
                      width={80}
                      height={32}
                      sx={{
                        borderRadius: 2,
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.08)",
                      }}
                    />
                    <Skeleton
                      variant="rectangular"
                      width={80}
                      height={32}
                      sx={{
                        borderRadius: 2,
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.08)",
                      }}
                    />
                  </Stack>
                </Stack>
              </Box>
              {index < 2 && (
                <Divider
                  sx={{
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.08)",
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
};

export default StoryManagementChaptersShimmer;

